import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { isV2PatientVersion } from '@odo/shared-ui';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import logo from '../assets/icons/odo.png';
import Loading from '../components/Loading';
import { Step, Stepper, useStepper } from '../components/navigation/Stepper';
import ConversationPanel from '../components/messages/ConversationPanel';
import { CHAT_QUERY_PARAM, CHAT_QUERY_OPEN } from '../utils/constants';
import {
    SubmissionFormProvider,
    useSubmissionForm
} from '../providers/patient/PatientSubmissionContext';
import { usePractice } from '../providers/practices/PracticesProvider';
import {
    buildPatientCreationSteps,
    getPathFromStep,
    getPathSegment,
    getStepIndexFromPath
} from './patientCreationSteps';

interface StepperContextType {
    currentStep: number;
    stepData: Record<string, any>;
    goToStep: (stepIndex: number) => void;
    goNext: (id?: string) => void;
    goBack: () => void;
    updateStepData: (stepId: string, data: any) => void;
    getStepData: (stepId: string) => any;
    resetStepper: () => void;
    steps: Step[];
    isV2: boolean;
}

const StepperContext = createContext<StepperContextType | undefined>(undefined);

export const useStepperContext = () => {
    const context = useContext(StepperContext);
    if (!context) {
        throw new Error('useStepperContext must be used within a PatientCreationLayout');
    }
    return context;
};

interface LayoutFooterContextType {
    setFooterContent: (content: React.ReactNode) => void;
}

const LayoutFooterContext = createContext<LayoutFooterContextType | undefined>(undefined);

export const useLayoutFooter = () => {
    const context = useContext(LayoutFooterContext);
    if (!context) {
        throw new Error('useLayoutFooter must be used within a PatientCreationLayout');
    }
    return context;
};

interface CreationChatContextType {
    openChat: () => void;
}

const CreationChatContext = createContext<CreationChatContextType | undefined>(undefined);

export const useCreationChat = () => {
    const context = useContext(CreationChatContext);
    if (!context) {
        throw new Error('useCreationChat must be used within a PatientCreationLayout');
    }
    return context;
};

interface LayoutProps {
    children: React.ReactNode;
}

interface PatientCreationLayoutInnerProps extends LayoutProps {
    id?: string;
}

const PatientCreationLayoutInner: React.FC<PatientCreationLayoutInnerProps> = ({
    children,
    id
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { isLoading, formData } = useSubmissionForm();
    const { practices, isLoading: isPracticesLoading } = usePractice();

    const isV2 = isV2PatientVersion(
        practices.find((practice) => practice.id === formData?.practiceId)?.version
    );
    const steps = useMemo(() => buildPatientCreationSteps(isV2), [isV2]);
    const currentStepFromUrl = getStepIndexFromPath(steps, location.pathname);
    const stepperState = useStepper(currentStepFromUrl);
    const [footerContent, setFooterContent] = useState<React.ReactNode>(null);
    const [chatOpen, setChatOpen] = useState(false);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const hasAutoOpenedChat = useRef(false);

    useEffect(() => {
        if (
            id &&
            searchParams.get(CHAT_QUERY_PARAM) === CHAT_QUERY_OPEN &&
            !hasAutoOpenedChat.current
        ) {
            hasAutoOpenedChat.current = true;
            setChatOpen(true);
        }
    }, [id, searchParams]);

    const openChat = useCallback(() => {
        setChatOpen(true);
    }, []);

    const handleStepChange = useCallback(
        (stepIndex: number, newId?: string) => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
            const newPath = getPathFromStep(steps, stepIndex, newId || id || '');
            navigate(newPath);
        },
        [navigate, id, steps]
    );

    const handleBack = useCallback(() => {
        const prevStepIndex = currentStepFromUrl - 1;
        if (prevStepIndex >= 0) {
            handleStepChange(prevStepIndex);
        } else {
            navigate('/home');
        }
    }, [navigate, currentStepFromUrl, handleStepChange]);

    const handleNext = useCallback(
        (newId?: string) => {
            const nextStepIndex = currentStepFromUrl + 1;
            if (nextStepIndex < steps.length) {
                handleStepChange(nextStepIndex, newId);
            } else {
                navigate('/home');
            }
        },
        [navigate, currentStepFromUrl, handleStepChange, steps.length]
    );

    useEffect(() => {
        stepperState.goToStep(currentStepFromUrl);
    }, [currentStepFromUrl]);

    const stepperContextValue: StepperContextType = {
        ...stepperState,
        goToStep: handleStepChange,
        goNext: handleNext,
        steps,
        isV2
    };

    if (isLoading || isPracticesLoading) {
        return <Loading text="Loading..." />;
    }

    if (id && steps[currentStepFromUrl].id !== getPathSegment(location.pathname)) {
        return <Navigate to={getPathFromStep(steps, currentStepFromUrl, id)} replace />;
    }

    return (
        <StepperContext.Provider value={stepperContextValue}>
            <LayoutFooterContext.Provider value={{ setFooterContent }}>
                <CreationChatContext.Provider value={{ openChat }}>
                    <div className="flex flex-col w-full h-screen bg-gray-100 overflow-hidden">
                        {/* Header */}
                        <div className="relative flex justify-center items-center px-32 py-4">
                            <div className="absolute left-32">
                                <button
                                    onClick={handleBack}
                                    className="flex items-center gap-1 text-base font-normal text-gray-500 hover:underline"
                                >
                                    <ChevronLeftIcon className="h-5 w-5" />
                                    <span>{steps[currentStepFromUrl].backLabel || 'Back'}</span>
                                </button>
                            </div>
                            <img
                                src={logo}
                                alt="logo"
                                className="h-10 w-auto cursor-pointer"
                                onClick={() => navigate('/home')}
                            />
                        </div>

                        {/* White background div for content */}
                        <div className="flex-1 flex justify-center px-32 pb-12 overflow-hidden">
                            <div className="w-full bg-white rounded-lg shadow-sm pb-2 flex flex-col h-full">
                                {/* Stepper */}
                                <div className="flex-shrink-0 flex justify-center pb-8 pt-8">
                                    <Stepper
                                        steps={steps}
                                        currentStep={currentStepFromUrl}
                                        onStepChange={handleStepChange}
                                        className="w-full max-w-4xl"
                                    />
                                </div>
                                <div
                                    ref={scrollContainerRef}
                                    className="flex-1 overflow-y-auto scrollbar-hide"
                                >
                                    {children}
                                    <div className="flex justify-center items-center pb-8"></div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white h-24 w-full flex items-center px-32">
                            {footerContent}
                        </div>
                    </div>
                </CreationChatContext.Provider>
            </LayoutFooterContext.Provider>
            {chatOpen && id && (
                <ConversationPanel
                    patient={{
                        id,
                        name:
                            [formData?.firstName, formData?.lastName].filter(Boolean).join(' ') ||
                            'New patient',
                        treatmentStatus: null,
                        startDate: null,
                        alignerNumber: null,
                        alignerTotal: null,
                        requiredAction: 'Submit case'
                    }}
                    onClose={() => setChatOpen(false)}
                />
            )}
        </StepperContext.Provider>
    );
};

const PatientCreationLayoutContent: React.FC<LayoutProps> = ({ children }) => {
    const { id } = useParams<{ id: string }>();

    return <PatientCreationLayoutInner id={id}>{children}</PatientCreationLayoutInner>;
};

const PatientCreationLayout: React.FC<LayoutProps> = ({ children }) => (
    <SubmissionFormProvider>
        <PatientCreationLayoutContent>{children}</PatientCreationLayoutContent>
    </SubmissionFormProvider>
);

export { PatientCreationLayout };
